import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faChartGantt } from '@fortawesome/free-solid-svg-icons';

const Container = styled.div`
  padding: 2rem;
  background: #ffffff;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
`;

const Select = styled.select`
  padding: 0.6rem 1.2rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  option {
    background: #ffffff;
    color: var(--text-primary);
  }
`;

const Button = styled(motion.button)`
  padding: 0.6rem 1.2rem;
  background: ${props => props.primary ? 'var(--gradient-primary)' : '#ffffff'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.03)'};
    border-color: ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.2)'};
  }
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  gap: 1.5rem;
`;

const Card = styled(motion.div)`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: ${props => props.glowColor || 'var(--primary)'};
    filter: blur(80px);
    opacity: 0.08;
    top: -30px;
    right: -30px;
  }
  
  ${props => props.gridArea && `grid-area: ${props.gridArea};`}
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const CardControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 102, 255, 0.1);
  border: 1px solid rgba(0, 102, 255, 0.1);
  color: var(--text-primary);
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 102, 255, 0.2);
    border-color: rgba(0, 102, 255, 0.2);
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 240px;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
  position: relative;
  overflow: hidden;
`;

// Mock Line Chart Component
const LineChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 60%;
    background: linear-gradient(to top, rgba(0, 102, 255, 0.1), transparent);
    border-radius: 0 0 16px 16px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 30%;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(0, 102, 255, 0.3);
    z-index: 1;
  }
`;

const LinePath = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100px;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 3px;
    background: var(--primary);
    bottom: 50px;
    
    clip-path: ${props => props.path || 'polygon(0% 0%, 10% 50%, 25% 20%, 40% 70%, 60% 40%, 75% 60%, 90% 10%, 100% 40%, 100% 100%, 0% 100%)'};
  }
`;

const LinePoints = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 10px;
`;

const DataPoint = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  position: relative;
  margin-bottom: ${props => props.height || '50px'};
  
  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(0, 102, 255, 0.3);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

// Mock Pie Chart Component
const PieChart = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PieContainer = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: conic-gradient(
    var(--primary) 0% 30%,
    var(--accent) 30% 55%,
    var(--warning) 55% 70%,
    var(--success) 70% 85%,
    var(--error) 85% 100%
  );
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: #ffffff;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const PieLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.color};
`;

// Mock Bar Chart Component
const BarChart = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  padding: 0 10px;
`;

const Bar = styled.div`
  width: 30px;
  height: ${props => props.height};
  background: ${props => props.color || 'var(--primary)'};
  border-radius: 6px 6px 0 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 30%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent);
    border-radius: 6px 6px 0 0;
  }
`;

// Stats Card Component
const StatsCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const StatsValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 1rem 0;
  
  span {
    font-size: 1rem;
    color: var(--text-secondary);
    margin-left: 0.5rem;
  }
`;

const StatsTrend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${props => props.positive ? 'var(--success)' : 'var(--error)'};
`;

const TrendIcon = styled.span`
  font-size: 1.2rem;
`;

const StatsChart = styled.div`
  height: 60px;
  margin-top: auto;
  background-image: ${props => props.chart || 'linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%)'};
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 10px,
      rgba(255, 255, 255, 0.05) 10px,
      rgba(255, 255, 255, 0.05) 20px
    );
  }
`;

// Prediction Card Component
const PredictionCard = styled(Card)`
  grid-column: span 3;
`;

const PredictionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const AILabel = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  background: rgba(0, 102, 255, 0.2);
  color: var(--primary-light);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PredictionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const PredictionItem = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 12px;
  padding: 1.2rem;
  border-left: 3px solid ${props => props.color || 'var(--primary)'};
`;

const PredictionTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const PredictionValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PredictionDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

const PredictionConfidence = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.8rem;
  font-size: 0.85rem;
`;

const ConfidenceBar = styled.div`
  width: 100px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin: 0 0.8rem;
  overflow: hidden;
  
  &::before {
    content: '';
    display: block;
    width: ${props => props.value || '70%'};
    height: 100%;
    background: ${props => props.color || 'var(--primary)'};
    border-radius: 3px;
  }
`;

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('month');

  return (
    <Container>

      <PageHeaderWithInfo
        icon={faChartGantt}
        title="Predictive Analytics"
        description={null}
      >
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
            <option value="quarter">Past Quarter</option>
            <option value="year">Past Year</option>
          </Select>
          <Button>Export Report</Button>
        </div>
      </PageHeaderWithInfo>

      <AnalyticsGrid>
        <Card
          gridArea="1 / 1 / 2 / 2"
          glowColor="var(--primary)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <CardHeader>
            <CardTitle>Policy Performance</CardTitle>
            <CardControls>
              <IconButton title="Download">📥</IconButton>
              <IconButton title="Expand">⤢</IconButton>
            </CardControls>
          </CardHeader>

          <ChartContainer>
            <LineChart>
              <LinePath />
              <LinePoints>
                {[65, 40, 85, 30, 70, 50, 90].map((height, index) => (
                  <DataPoint key={index} height={`${height}px`} />
                ))}
              </LinePoints>
            </LineChart>
          </ChartContainer>
        </Card>

        <Card
          gridArea="1 / 2 / 2 / 3"
          glowColor="var(--accent)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <CardHeader>
            <CardTitle>Claims Distribution</CardTitle>
            <CardControls>
              <IconButton title="Download">📥</IconButton>
              <IconButton title="Expand">⤢</IconButton>
            </CardControls>
          </CardHeader>

          <ChartContainer>
            <PieChart>
              <div>
                <PieContainer />
                <PieLegend>
                  <LegendItem>
                    <LegendColor color="var(--primary)" />
                    <span>Auto</span>
                  </LegendItem>
                  <LegendItem>
                    <LegendColor color="var(--accent)" />
                    <span>Property</span>
                  </LegendItem>
                  <LegendItem>
                    <LegendColor color="var(--warning)" />
                    <span>Liability</span>
                  </LegendItem>
                  <LegendItem>
                    <LegendColor color="var(--success)" />
                    <span>Cyber</span>
                  </LegendItem>
                  <LegendItem>
                    <LegendColor color="var(--error)" />
                    <span>Other</span>
                  </LegendItem>
                </PieLegend>
              </div>
            </PieChart>
          </ChartContainer>
        </Card>

        <Card
          gridArea="1 / 3 / 2 / 4"
          glowColor="var(--warning)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <CardHeader>
            <CardTitle>Risk Severity Analysis</CardTitle>
            <CardControls>
              <IconButton title="Download">📥</IconButton>
              <IconButton title="Expand">⤢</IconButton>
            </CardControls>
          </CardHeader>

          <ChartContainer>
            <BarChart>
              <Bar height="60%" color="var(--success)" />
              <Bar height="85%" color="var(--success)" />
              <Bar height="120%" color="var(--warning)" />
              <Bar height="170%" color="var(--warning)" />
              <Bar height="135%" color="var(--warning)" />
              <Bar height="190%" color="var(--error)" />
              <Bar height="75%" color="var(--success)" />
            </BarChart>
          </ChartContainer>
        </Card>

        <StatsCard
          gridArea="2 / 1 / 3 / 2"
          glowColor="var(--success)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <CardTitle>Premium Revenue</CardTitle>
          <StatsValue>$1.85M <span>This month</span></StatsValue>
          <StatsTrend positive>
            <TrendIcon>↗</TrendIcon>
            <span>12.4% increase vs. last month</span>
          </StatsTrend>
          <StatsChart chart="linear-gradient(90deg, rgba(16, 185, 129, 0.8) 0%, var(--primary) 100%)" />
        </StatsCard>

        <StatsCard
          gridArea="2 / 2 / 3 / 3"
          glowColor="var(--warning)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <CardTitle>Claims Payout</CardTitle>
          <StatsValue>$685K <span>This month</span></StatsValue>
          <StatsTrend positive={false}>
            <TrendIcon>↗</TrendIcon>
            <span>8.7% increase vs. last month</span>
          </StatsTrend>
          <StatsChart chart="linear-gradient(90deg, var(--warning) 0%, var(--error) 100%)" />
        </StatsCard>

        <StatsCard
          gridArea="2 / 3 / 3 / 4"
          glowColor="var(--accent)"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <CardTitle>Fraud Detection</CardTitle>
          <StatsValue>$124K <span>Prevented losses</span></StatsValue>
          <StatsTrend positive>
            <TrendIcon>↗</TrendIcon>
            <span>15.3% improvement in detection</span>
          </StatsTrend>
          <StatsChart chart="linear-gradient(90deg, var(--accent) 0%, var(--primary) 100%)" />
        </StatsCard>

        <PredictionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <PredictionHeader>
            <CardTitle>AI-Generated Predictions</CardTitle>
            <AILabel>
              <span>🤖</span>
              <span>Powered by Advanced ML Models</span>
            </AILabel>
          </PredictionHeader>

          <PredictionGrid>
            <PredictionItem color="var(--primary)">
              <PredictionTitle>Customer Retention</PredictionTitle>
              <PredictionValue>92.3%</PredictionValue>
              <PredictionDescription>
                Predicted retention rate for next quarter based on current policy satisfaction, claim resolution speed, and pricing competitiveness.
              </PredictionDescription>
              <PredictionConfidence>
                Confidence:
                <ConfidenceBar value="88%" color="var(--primary)" />
                88%
              </PredictionConfidence>
            </PredictionItem>

            <PredictionItem color="var(--warning)">
              <PredictionTitle>Emerging Risk Areas</PredictionTitle>
              <PredictionValue>Cyber Attacks</PredictionValue>
              <PredictionDescription>
                Significant increase in ransomware incidents predicted for Q3 in financial sector clients. Recommend proactive security assessments.
              </PredictionDescription>
              <PredictionConfidence>
                Confidence:
                <ConfidenceBar value="75%" color="var(--warning)" />
                75%
              </PredictionConfidence>
            </PredictionItem>

            <PredictionItem color="var(--success)">
              <PredictionTitle>Premium Growth</PredictionTitle>
              <PredictionValue>8.5%</PredictionValue>
              <PredictionDescription>
                Projected premium growth for next quarter based on market trends, renewals, and new business opportunities identified by AI.
              </PredictionDescription>
              <PredictionConfidence>
                Confidence:
                <ConfidenceBar value="82%" color="var(--success)" />
                82%
              </PredictionConfidence>
            </PredictionItem>
          </PredictionGrid>
        </PredictionCard>
      </AnalyticsGrid>
    </Container>
  );
};

export default Analytics;
